<script>
  import { link } from 'svelte-spa-router';
  import { Landmark, HelpCircle, Upload, Download, Trash2, AlertTriangle } from '../lib/icons/index.js';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import SuccessBanner from '../components/SuccessBanner.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import SideSheet from '../components/SideSheet.svelte';
  import BilancioUploadSheet from '../components/BilancioUploadSheet.svelte';
  import FeatureBadge from '../components/FeatureBadge.svelte';
  import { featureKind } from '../lib/features.js';
  import { bilanciApi } from '../stores/api.js';
  import { activeCompanyId, companiesLoading } from '../stores/company.js';
  import { capabilities } from '../stores/capabilities.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { formatDate, formatDateTime } from '../lib/labels.js';
  import { bilancioPermissions, fileNeedsRepair } from '../lib/bilancio.js';

  let bilanci = [];
  let loading = true;
  let error = null;
  let success = '';
  let successLink = null;
  let showIntro = false;
  // Open by itself while there is nothing else to read, until it is toggled.
  let introTouched = false;

  let uploadOpen = false;
  let repairFor = null;

  let removing = null;
  let removeBusy = false;
  let removeError = null;

  let downloadError = null;

  $: companyId = $activeCompanyId;
  $: permissions = bilancioPermissions($capabilities?.role);
  $: if (companyId) load(companyId);
  $: if (!companyId) { bilanci = []; loading = $companiesLoading; }
  $: needsRepair = bilanci.filter(fileNeedsRepair);

  async function load(id) {
    loading = true;
    error = null;
    try {
      const response = await bilanciApi.list(id);
      if (id !== companyId) return;
      bilanci = response.bilanci ?? [];
      if (!introTouched) showIntro = bilanci.length === 0;
    } catch (err) {
      if (id !== companyId) return;
      error = err;
      bilanci = [];
    } finally {
      if (id === companyId) loading = false;
    }
  }

  function openUpload(target = null) {
    repairFor = target;
    success = '';
    successLink = null;
    uploadOpen = true;
  }

  function onUploaded(event) {
    const { outcome, bilancio, replaced } = event.detail;
    uploadOpen = false;
    repairFor = null;
    const year = bilancio?.fiscalYear ?? '';
    if (outcome === 'created') success = tImmediate(replaced ? 'bilancio.upload.done.replaced' : 'bilancio.upload.done.created', { year });
    else if (outcome === 'restored') success = tImmediate('bilancio.upload.done.restored', { year });
    else success = tImmediate('bilancio.upload.done.unchanged', { year });
    successLink = bilancio?.id ? `/bilancio/${bilancio.id}` : null;
    load(companyId);
  }

  function askRemove(bilancio) {
    removing = bilancio;
    removeError = null;
    success = '';
    successLink = null;
  }

  async function confirmRemove() {
    if (!removing) return;
    removeBusy = true;
    removeError = null;
    try {
      const { purgeAfter } = await bilanciApi.remove(companyId, removing.id);
      success = purgeAfter
        ? tImmediate('bilancio.remove.done', { year: removing.fiscalYear, date: formatDate(purgeAfter) })
        : tImmediate('bilancio.remove.doneKept', { year: removing.fiscalYear });
      removing = null;
      await load(companyId);
    } catch (err) {
      removeError = err;
    } finally {
      removeBusy = false;
    }
  }

  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  async function download(bilancio) {
    downloadError = null;
    try {
      const { blob, filename } = await bilanciApi.downloadFile(companyId, bilancio.id);
      saveBlob(blob, filename);
    } catch (err) {
      // A copy that failed its check is not an error to read: the download
      // has just recorded what it found, and the list explains the repair.
      if (err.code === 'bilancio_file_integrity') load(companyId);
      else downloadError = err;
    }
  }

  const warningsLabel = (count) => {
    if (!count) return $t('bilancio.list.warningsNone');
    return count === 1 ? $t('bilancio.list.warningsOne') : $t('bilancio.list.warnings', { n: count });
  };
</script>

<Layout>
  <div class="view-head">
    <div class="view-head-copy">
      <h1 class="section-title">{$t('bilancio.title')}</h1>
      <p class="view-subtitle">{$t('bilancio.subtitle')}</p>
      <!-- Experimental feature: said on the page itself, with what it means. -->
      <p class="mt-2 text-sm text-muted measure-wide" data-testid="feature-note">
        <FeatureBadge kind={featureKind('bilancio')} /> {$t('features.bilancio.description')}
      </p>
    </div>
    {#if permissions.upload}
      <div class="view-actions">
        <button type="button" class="btn btn-primary gap-2" on:click={() => openUpload()}>
          <Upload size={16} aria-hidden="true" />
          {$t('bilancio.actions.upload')}
        </button>
      </div>
    {/if}
  </div>

  <!-- Feature description: someone who has never seen a bilancio XBRL has to
       be able to learn here what it is and where to get it. Open while the
       page has nothing to show, collapsed once there is a list. -->
  <div class="mb-6">
    <button type="button" class="btn-link -ml-1" on:click={() => { introTouched = true; showIntro = !showIntro; }}
      aria-expanded={showIntro} aria-controls="bilancio-intro">
      <HelpCircle size={16} aria-hidden="true" />
      {showIntro ? $t('bilancio.intro.toggleHide') : $t('bilancio.intro.toggleShow')}
    </button>
    {#if showIntro}
      <div id="bilancio-intro" class="next-step mt-2 space-y-4 text-sm measure-wide">
        <h2 class="text-heading">{$t('bilancio.intro.title')}</h2>
        {#each ['what', 'where', 'does', 'doesNot'] as part}
          <div>
            <p class="font-bold text-heading">{$t(`bilancio.intro.${part}Title`)}</p>
            <p class="mt-1 text-muted">{$t(`bilancio.intro.${part}`)}</p>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <SuccessBanner message={success} />
  {#if success && successLink}
    <p class="-mt-2 mb-4 text-sm"><a href={successLink} use:link class="btn-link">{$t('bilancio.upload.done.open')}</a></p>
  {/if}
  <ErrorBanner error={downloadError} />

  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} title={$t('bilancio.loadError')} />
  {:else}
    {#each needsRepair as broken (broken.id)}
      <div class="alert alert-warning mb-4" role="status">
        <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
        <div class="min-w-0 flex-1">
          <p class="alert-title">{$t('bilancio.repair.title')}</p>
          <p class="alert-message mt-1">
            {permissions.upload
              ? $t('bilancio.repair.body', { year: broken.fiscalYear })
              : $t('bilancio.repair.bodyReadonly', { year: broken.fiscalYear })}
          </p>
          {#if permissions.upload}
            <button type="button" class="btn btn-secondary btn-sm mt-3 gap-2" on:click={() => openUpload(broken)}>
              <Upload size={14} aria-hidden="true" />
              {$t('bilancio.actions.repair')}
            </button>
          {/if}
        </div>
      </div>
    {/each}

    {#if bilanci.length === 0}
      <EmptyState icon={Landmark} title={$t('bilancio.empty.title')}
        body={permissions.upload ? $t('bilancio.empty.body') : $t('bilancio.empty.bodyReadonly')}>
        {#if permissions.upload}
          <button type="button" class="btn btn-primary gap-2" on:click={() => openUpload()}>
            <Upload size={16} aria-hidden="true" />
            {$t('bilancio.actions.upload')}
          </button>
        {/if}
      </EmptyState>
    {:else}
      <section aria-labelledby="bilancio-list-title">
        <div class="section-heading">
          <h2 id="bilancio-list-title">{$t('bilancio.list.heading')}</h2>
          <span class="result-count">
            {bilanci.length === 1 ? $t('bilancio.list.countOne') : $t('bilancio.list.count', { n: bilanci.length })}
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="table-modern table-stack w-full" role="table">
            <thead class="table-header" role="rowgroup">
              <tr role="row">
                <th class="table-cell table-header-cell text-left" role="columnheader" scope="col">{$t('bilancio.list.columns.esercizio')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader" scope="col">{$t('bilancio.list.columns.forma')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader" scope="col">{$t('bilancio.list.columns.caricato')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader" scope="col">{$t('bilancio.list.columns.segnalazioni')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader" scope="col">{$t('bilancio.list.columns.file')}</th>
                <th class="table-cell table-header-cell text-right" role="columnheader" scope="col"><span class="sr-only">{$t('bilancio.list.columns.azioni')}</span></th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {#each bilanci as bilancio (bilancio.id)}
                <tr class="table-row" role="row" style="cursor: default;">
                  <td role="cell" data-label={$t('bilancio.list.columns.esercizio')} class="table-cell">
                    <a href={`/bilancio/${bilancio.id}`} use:link class="invoice-link font-bold"
                      aria-label={$t('bilancio.actions.open', { year: bilancio.fiscalYear })}>{bilancio.fiscalYear}</a>
                    <span class="block text-xs text-muted">
                      {$t('bilancio.list.period', { start: formatDate(bilancio.periodStart), end: formatDate(bilancio.periodEnd) })}
                    </span>
                  </td>
                  <td role="cell" data-label={$t('bilancio.list.columns.forma')} class="table-cell">
                    <span class="inline-flex items-center gap-1">
                      <span class="badge badge-gray">{$t(`bilancio.forma.${bilancio.schemaType}`)}</span>
                      <InfoHint text={$t(`bilancio.formaHelp.${bilancio.schemaType}`)} />
                    </span>
                  </td>
                  <td role="cell" data-label={$t('bilancio.list.columns.caricato')} class="table-cell text-xs">
                    {formatDateTime(bilancio.uploadedAt)}
                  </td>
                  <td role="cell" data-label={$t('bilancio.list.columns.segnalazioni')} class="table-cell text-xs">
                    {warningsLabel(bilancio.warnings?.length ?? 0)}
                  </td>
                  <td role="cell" data-label={$t('bilancio.list.columns.file')} class="table-cell">
                    <span class="inline-flex items-center gap-1">
                      <span class="badge {bilancio.fileStatus === 'ok' ? 'badge-green' : 'badge-red'}">
                        {$t(`bilancio.fileStatus.${bilancio.fileStatus}`)}
                      </span>
                      <InfoHint text={$t(`bilancio.fileStatusHelp.${bilancio.fileStatus}`)} />
                    </span>
                  </td>
                  <td role="cell" data-label="" class="table-cell text-right">
                    <div class="flex flex-wrap items-center justify-end gap-1">
                      {#if permissions.rawRead && bilancio.fileStatus === 'ok'}
                        <button type="button" class="icon-button" on:click={() => download(bilancio)}
                          aria-label={$t('bilancio.actions.downloadOf', { year: bilancio.fiscalYear })}
                          title={$t('bilancio.actions.download')}>
                          <Download size={16} aria-hidden="true" />
                        </button>
                      {/if}
                      {#if permissions.remove}
                        <button type="button" class="icon-button hover:text-danger" on:click={() => askRemove(bilancio)}
                          aria-label={$t('bilancio.actions.deleteOf', { year: bilancio.fiscalYear })}
                          title={$t('bilancio.actions.delete')}>
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      {/if}
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}
  {/if}

  {#if permissions.upload}
    <BilancioUploadSheet open={uploadOpen} {companyId} canReplace={permissions.replace} {repairFor}
      on:close={() => { uploadOpen = false; repairFor = null; }} on:done={onUploaded} />
  {/if}

  {#if permissions.remove}
    <SideSheet open={Boolean(removing)} id="bilancio-remove" eyebrow={$t('bilancio.remove.eyebrow')}
      title={removing ? $t('bilancio.remove.title', { year: removing.fiscalYear }) : ''}
      closeLabel={$t('bilancio.remove.cancel')} on:close={() => { if (!removeBusy) removing = null; }}>
      <p class="sheet-section-intro">{$t('bilancio.remove.body')}</p>
      <ErrorBanner error={removeError} />
      <svelte:fragment slot="footer">
        <button type="button" class="btn btn-secondary" disabled={removeBusy} on:click={() => (removing = null)}>
          {$t('bilancio.remove.cancel')}
        </button>
        <button type="button" class="btn btn-danger gap-2" disabled={removeBusy} on:click={confirmRemove}>
          <Trash2 size={16} aria-hidden="true" />
          {removeBusy ? $t('bilancio.remove.deleting') : $t('bilancio.remove.confirm')}
        </button>
      </svelte:fragment>
    </SideSheet>
  {/if}
</Layout>
